import moment from "moment";
import { CATEGORIES, LAND_AND_SHARED_OPTIONS } from "./haklaiConstants";
import {
  HAKLAI_DEPRECIATION_ERRORS,
  HAKLAI_DEPRECIATION_LABELS
} from "./haklaiDepreciationTexts";

export const HAKLAI_PROPERTY_DEPRECIATION_MODE = {
  NONE: "none",
  RESIDENTIAL_AUTO: "residentialAuto",
  MANUAL_FORM_11: "manualForm11"
};

export const HAKLAI_RESIDENTIAL_DEP_TYPE_OPTIONS = [
  { label: HAKLAI_DEPRECIATION_LABELS.exemptRentalTaxTrack, value: "ptor" },
  { label: HAKLAI_DEPRECIATION_LABELS.rentalIncomeTaxTrack, value: "percent10" }
];

const PTOR_START_DATE = "2007-02-27";
export const DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT = 0.02;
export const HAKLAI_RESIDENTIAL_DEPRECIATION_RATE_OPTIONS = [
  { label: "2%", value: DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT },
  { label: "4%", value: 0.04 }
];
const DEFAULT_RESIDENTIAL_BUILDING_PERCENT = 1;
const NON_DEPRECIABLE_LAND_AND_SHARED_ASSET_KEYS = new Set([
  "unutilizedBuildingRights"
]);
const LAND_AND_SHARED_ASSET_KEY_BY_LABEL = LAND_AND_SHARED_OPTIONS.reduce((acc, option) => {
  acc[option.label] = option.key;
  return acc;
}, {});

const normalizeNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const parseClassicDepreciationDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isResidentialDepType = (value) => ["ptor", "percent10"].includes(value);
const isResidentialDepreciationRate = (value) => {
  const numericValue = normalizeNumericValue(value);
  return HAKLAI_RESIDENTIAL_DEPRECIATION_RATE_OPTIONS.some((option) => option.value === numericValue);
};
const isValidResidentialBuildingPercent = (value) => {
  const numericValue = normalizeNumericValue(value);
  return numericValue !== null && numericValue > 0 && numericValue <= 1;
};

export const createHaklaiResidentialDepreciationRange = (overrides = {}) => ({
  startDate: "",
  endDate: "",
  ...overrides
});

const getNormalizedResidentialDepType = (depreciation = {}) => {
  return isResidentialDepType(depreciation?.depType) ? depreciation.depType : "";
};

const getNormalizedResidentialDepreciationPercent = (depreciation = {}) => {
  const topLevelDepreciationPercent = normalizeNumericValue(depreciation?.depreciationPercent);
  return isResidentialDepreciationRate(topLevelDepreciationPercent)
    ? topLevelDepreciationPercent
    : DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT;
};

const getNormalizedResidentialBuildingPercent = (depreciation = {}) => {
  const topLevelBuildingPercent = normalizeNumericValue(depreciation?.depreciationBuiltPercent);
  return isValidResidentialBuildingPercent(topLevelBuildingPercent)
    ? topLevelBuildingPercent
    : null;
};

const normalizeResidentialDateRange = (range = {}) => ({
  ...range,
  startDate: range?.startDate || "",
  endDate: range?.endDate || ""
});

const isPlainObject = (value) => (
  value && typeof value === "object" && !Array.isArray(value)
);

const getLandAndSharedAssetTypeKey = (part = {}) => {
  const assetTypeKey = String(part?.assetTypeKey || "").trim();
  if (assetTypeKey) return assetTypeKey;
  return LAND_AND_SHARED_ASSET_KEY_BY_LABEL[String(part?.label || "").trim()] || "";
};

export const isHaklaiExplicitlyNonDepreciableAsset = (part = {}) => {
  if (!part || typeof part !== "object") return true;
  if (part.category === CATEGORIES.LAND_AND_SHARED) {
    return NON_DEPRECIABLE_LAND_AND_SHARED_ASSET_KEYS.has(getLandAndSharedAssetTypeKey(part));
  }
  return false;
};

export const isHaklaiManualForm11DepreciationAllowed = (part = {}) => {
  if (!part || typeof part !== "object") return false;
  if (part.category === CATEGORIES.RESIDENTIAL) return false;
  if (isHaklaiExplicitlyNonDepreciableAsset(part)) return false;
  return Object.values(CATEGORIES).includes(part.category);
};

export const isHaklaiManualPurchaseDepreciationAllowed = (part = {}) => {
  if (!isHaklaiManualForm11DepreciationAllowed(part)) return false;
  return true;
};

export const getHaklaiResidentialDepreciationBuiltPercent = ({
  depreciation = {}
} = {}) => (
  getNormalizedResidentialBuildingPercent(depreciation)
  || DEFAULT_RESIDENTIAL_BUILDING_PERCENT
);

export const getHaklaiResidentialDepreciationPercent = ({
  depreciation = {}
} = {}) => getNormalizedResidentialDepreciationPercent(depreciation);

export const normalizeHaklaiDepreciationState = (depreciation) => {
  const normalizedDepreciation = isPlainObject(depreciation) ? depreciation : {};
  if (!Array.isArray(normalizedDepreciation.dateRanges)) return normalizedDepreciation;
  const normalizedBuildingPercent = getNormalizedResidentialBuildingPercent(normalizedDepreciation);

  return {
    ...normalizedDepreciation,
    depType: getNormalizedResidentialDepType(normalizedDepreciation),
    depreciationPercent: getNormalizedResidentialDepreciationPercent(normalizedDepreciation),
    ...(normalizedBuildingPercent !== null ? { depreciationBuiltPercent: normalizedBuildingPercent } : {}),
    dateRanges: normalizedDepreciation.dateRanges.map((range) => normalizeResidentialDateRange(range))
  };
};

export const getHaklaiPartDepreciationMode = (part = {}) => {
  if (!part || typeof part !== "object") return HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE;

  if (part.category === CATEGORIES.RESIDENTIAL) {
    return HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO;
  }

  if (isHaklaiManualForm11DepreciationAllowed(part)) {
    return HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
  }

  return HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE;
};

export const getHaklaiExpenseDepreciationMode = (part = {}) => {
  if (!part || typeof part !== "object") return HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE;

  if (part.category === CATEGORIES.RESIDENTIAL) {
    return HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO;
  }

  if (isHaklaiManualForm11DepreciationAllowed(part)) {
    return HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
  }

  return HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE;
};

export const isHaklaiResidentialRentalDepreciationProfile = (depreciation = {}) => {
  if (!isPlainObject(depreciation)) return false;
  if (Object.prototype.hasOwnProperty.call(depreciation, "depreciationResult")) return false;
  return (
    isResidentialDepType(depreciation.depType)
    || Array.isArray(depreciation.dateRanges)
  );
};

export const getHaklaiResidentialRentalProfile = ({
  partKey = "",
  part = {},
  residentialRentalByPart = {}
} = {}) => {
  if (!partKey || getHaklaiPartDepreciationMode(part) !== HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    return {};
  }

  const directProfile = residentialRentalByPart?.[partKey];
  return isHaklaiResidentialRentalDepreciationProfile(directProfile)
    ? normalizeHaklaiDepreciationState(directProfile)
    : {};
};

export const normalizeHaklaiResidentialRentalByPart = (
  residentialRentalByPart = {},
  propertyPartsData = {}
) => {
  const directProfiles = isPlainObject(residentialRentalByPart) ? residentialRentalByPart : {};
  const partKeys = new Set(Object.keys(directProfiles));

  return [...partKeys].reduce((acc, partKey) => {
    const part = propertyPartsData?.[partKey];
    if (getHaklaiPartDepreciationMode(part) !== HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
      return acc;
    }

    const profile = getHaklaiResidentialRentalProfile({
      partKey,
      part,
      residentialRentalByPart: directProfiles
    });
    if (Object.keys(profile).length > 0) {
      acc[partKey] = profile;
    }
    return acc;
  }, {});
};

export const isHaklaiPropertyDepreciationRelevant = (part = {}) => (
  getHaklaiPartDepreciationMode(part) !== HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE
);

export const HAKLAI_DEPRECIATION_BLOCKING_REASONS = {
  NONE: "none",
  SOLD_DATE: "soldDate",
  PURCHASE_DATE: "purchaseDate",
  ACQUISITION_AMOUNT: "acquisitionAmount",
  SALE_VALUE: "saleValue",
  BUILT_VALUE: "builtValue",
  BUILT_EXCEEDS_SALE: "builtExceedsSale"
};

const HAKLAI_DEPRECIATION_BLOCKING_REASON_MESSAGES = {
  soldDate: HAKLAI_DEPRECIATION_ERRORS.missingSoldDate,
  purchaseDate: HAKLAI_DEPRECIATION_ERRORS.missingPurchaseDetails,
  acquisitionAmount: HAKLAI_DEPRECIATION_ERRORS.missingAcquisitionAmount,
  saleValue: HAKLAI_DEPRECIATION_ERRORS.missingSaleValue,
  builtValue: HAKLAI_DEPRECIATION_ERRORS.missingBuiltValue,
  builtExceedsSale: HAKLAI_DEPRECIATION_ERRORS.builtValueExceedsSale
};

export const getHaklaiDepreciationBlockingReasonMessage = (reason) => (
  HAKLAI_DEPRECIATION_BLOCKING_REASON_MESSAGES[reason] || ""
);

export const getHaklaiDepreciationPrerequisites = ({
  part = {},
  soldPropertyDate = "",
  purchaseDate = "",
  computedAcquisitionAmount = 0,
  modeOverride = ""
}) => {
  const mode = modeOverride || getHaklaiPartDepreciationMode(part);
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE) {
    return { ok: true, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.NONE };
  }

  if (!soldPropertyDate) {
    return { ok: false, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.SOLD_DATE };
  }
  if (!purchaseDate) {
    return { ok: false, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.PURCHASE_DATE };
  }
  const acquisitionAmount = normalizeNumericValue(computedAcquisitionAmount);
  if (!(acquisitionAmount !== null && acquisitionAmount > 0)) {
    return { ok: false, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.ACQUISITION_AMOUNT };
  }

  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    const partSaleValue = normalizeNumericValue(part?.value);
    if (!(partSaleValue !== null && partSaleValue > 0)) {
      return { ok: false, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.SALE_VALUE };
    }
  }

  return { ok: true, reason: HAKLAI_DEPRECIATION_BLOCKING_REASONS.NONE };
};

export const getHaklaiPropertyDepreciationStatus = ({
  part = {},
  depreciation = {},
  soldPropertyDate = "",
  ownershipStartDate = "",
  purchaseDate = "",
  computedAcquisitionAmount = 0,
  modeOverride = ""
}) => {
  const mode = modeOverride || getHaklaiPartDepreciationMode(part);
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE) return "notRelevant";

  const prerequisites = getHaklaiDepreciationPrerequisites({
    part,
    soldPropertyDate,
    purchaseDate,
    computedAcquisitionAmount,
    modeOverride: mode
  });
  if (!prerequisites.ok) return "blocked";

  const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
  if (!Object.keys(normalizedDepreciation).length) return "notConfigured";

  const validationError = getHaklaiPropertyDepreciationValidationError({
    part,
    depreciation: normalizedDepreciation,
    soldPropertyDate,
    ownershipStartDate,
    modeOverride: mode
  });
  return validationError ? "partial" : "ready";
};

const validateManualDepreciationAmount = (depreciation = {}) => {
  const depreciationResult = normalizeNumericValue(depreciation?.depreciationResult);
  if (depreciationResult === null || depreciationResult <= 0) {
    return HAKLAI_DEPRECIATION_ERRORS.depreciationAmountInvalid;
  }
  return "";
};

export const hasHaklaiManualForm11DepreciationInput = (depreciation = {}) => (
  isPlainObject(depreciation)
  && Object.prototype.hasOwnProperty.call(depreciation, "depreciationResult")
);

export const hasHaklaiDepreciationProfileInput = (depreciation = {}) => (
  isPlainObject(depreciation)
  && Object.keys(normalizeHaklaiDepreciationState(depreciation)).length > 0
);

export const hasHaklaiSelectablePurchaseDepreciationProfile = ({
  partKey = "",
  part = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {}
} = {}) => {
  const mode = getHaklaiPartDepreciationMode(part);
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    return hasHaklaiDepreciationProfileInput(getHaklaiResidentialRentalProfile({
      partKey,
      part,
      residentialRentalByPart
    }));
  }
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11) {
    return (
      isHaklaiManualPurchaseDepreciationAllowed(part)
      && hasHaklaiManualForm11DepreciationInput(purchaseDepreciationByPart?.[partKey])
      && hasHaklaiDepreciationProfileInput(purchaseDepreciationByPart?.[partKey])
    );
  }
  return false;
};

export const getHaklaiManualForm11DepreciationValidationError = (depreciation = {}) => (
  validateManualDepreciationAmount(normalizeHaklaiDepreciationState(depreciation))
);

export const getHaklaiPropertyPurchaseDepreciationResult = ({
  part = {},
  depreciation = {},
  acquisitionAmount = 0,
  soldPropertyDate = "",
  ownershipStartDate = "",
  modeOverride = ""
}) => {
  const mode = modeOverride || getHaklaiPartDepreciationMode(part);
  const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
  if (!Object.keys(normalizedDepreciation).length) return null;

  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    return calculateHaklaiAutoDepreciation({
      amount: acquisitionAmount,
      part,
      depreciation: normalizedDepreciation,
      acquisitionAmount,
      clampStartDate: ownershipStartDate || "",
      clampEndDate: soldPropertyDate || ""
    });
  }

  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11) {
    return normalizeNumericValue(normalizedDepreciation.depreciationResult);
  }

  return null;
};

export const validateDateRanges = ({
  dateRanges = [],
  lowerBoundDate = "",
  upperBoundDate = "",
  todayDate = moment(new Date()).format("YYYY-MM-DD")
}) => {
  const normalizedRanges = Array.isArray(dateRanges) ? dateRanges : [];
  if (!normalizedRanges.length) {
    return HAKLAI_DEPRECIATION_ERRORS.missingRentalRange;
  }

  for (let index = 0; index < normalizedRanges.length; index += 1) {
    const range = normalizedRanges[index] || {};
    if (!range.startDate) return HAKLAI_DEPRECIATION_ERRORS.startDateRequired;
    if (!range.endDate) return HAKLAI_DEPRECIATION_ERRORS.endDateRequired;
    if (range.endDate > todayDate) return HAKLAI_DEPRECIATION_ERRORS.endDateFuture;
    if (range.startDate > range.endDate) return HAKLAI_DEPRECIATION_ERRORS.startDateAfterEndDate;
    if (lowerBoundDate && range.startDate < lowerBoundDate) {
      return HAKLAI_DEPRECIATION_ERRORS.startDateBeforePurchase;
    }
    if (upperBoundDate && range.endDate > upperBoundDate) {
      return HAKLAI_DEPRECIATION_ERRORS.endDateAfterSale;
    }
    if (index > 0 && range.startDate <= normalizedRanges[index - 1].endDate) {
      return HAKLAI_DEPRECIATION_ERRORS.dateRangesOverlap;
    }
  }

  return "";
};

export const getHaklaiDepreciationFieldErrors = ({
  mode,
  dateRanges = [],
  depType = "",
  depreciationBuiltPercent = null,
  depreciationResult = null,
  lowerBoundDate = "",
  upperBoundDate = "",
  todayDate = moment(new Date()).format("YYYY-MM-DD")
}) => {
  const result = {
    dateRanges: [],
    depType: "",
    depreciationBuiltPercent: "",
    depreciationResult: ""
  };

  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    const normalizedRanges = Array.isArray(dateRanges) ? dateRanges : [];
    let prevEndDate = "";
    result.dateRanges = normalizedRanges.map((range, index) => {
      const rangeErrors = { startDate: "", endDate: "" };
      const currentRange = range || {};

      if (!currentRange.startDate) {
        rangeErrors.startDate = HAKLAI_DEPRECIATION_ERRORS.startDateRequired;
      } else if (lowerBoundDate && currentRange.startDate < lowerBoundDate) {
        rangeErrors.startDate = HAKLAI_DEPRECIATION_ERRORS.startDateBeforePurchase;
      } else if (index > 0 && prevEndDate && currentRange.startDate <= prevEndDate) {
        rangeErrors.startDate = HAKLAI_DEPRECIATION_ERRORS.dateRangesOverlap;
      }

      if (!currentRange.endDate) {
        rangeErrors.endDate = HAKLAI_DEPRECIATION_ERRORS.endDateRequired;
      } else if (currentRange.endDate > todayDate) {
        rangeErrors.endDate = HAKLAI_DEPRECIATION_ERRORS.endDateFuture;
      } else if (currentRange.startDate && currentRange.startDate > currentRange.endDate) {
        rangeErrors.endDate = HAKLAI_DEPRECIATION_ERRORS.startDateAfterEndDate;
      } else if (upperBoundDate && currentRange.endDate > upperBoundDate) {
        rangeErrors.endDate = HAKLAI_DEPRECIATION_ERRORS.endDateAfterSale;
      }

      if (currentRange.endDate) prevEndDate = currentRange.endDate;
      return rangeErrors;
    });

    if (!["ptor", "percent10"].includes(depType)) {
      result.depType = HAKLAI_DEPRECIATION_ERRORS.taxTrackRequired;
    }

    if (!isValidResidentialBuildingPercent(depreciationBuiltPercent)) {
      result.depreciationBuiltPercent = HAKLAI_DEPRECIATION_ERRORS.buildingPercentInvalid;
    }
  } else if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11) {
    const normalizedAmount = normalizeNumericValue(depreciationResult);
    if (!(normalizedAmount !== null && normalizedAmount > 0)) {
      result.depreciationResult = HAKLAI_DEPRECIATION_ERRORS.depreciationAmountInvalid;
    }
  }

  return result;
};

export const hasHaklaiDepreciationFieldErrors = (fieldErrors) => {
  if (!fieldErrors) return false;
  if (fieldErrors.depType) return true;
  if (fieldErrors.depreciationBuiltPercent) return true;
  if (fieldErrors.depreciationResult) return true;
  if (Array.isArray(fieldErrors.dateRanges)) {
    return fieldErrors.dateRanges.some((rangeErrors) => (
      Boolean(rangeErrors?.startDate) || Boolean(rangeErrors?.endDate)
    ));
  }
  return false;
};

export const getHaklaiPropertyDepreciationValidationError = ({
  part = {},
  depreciation = {},
  soldPropertyDate = "",
  ownershipStartDate = "",
  modeOverride = ""
}) => {
  const mode = modeOverride || getHaklaiPartDepreciationMode(part);
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.NONE) return "";

  const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
  // Empty depreciation payload means "user chose not to enter depreciation".
  // This is allowed; only partially-entered / invalid data should block flow.
  if (!Object.keys(normalizedDepreciation).length) return "";
  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO) {
    const rangeError = validateDateRanges({
      dateRanges: normalizedDepreciation.dateRanges,
      lowerBoundDate: ownershipStartDate,
      upperBoundDate: soldPropertyDate
    });
    if (rangeError) return rangeError;

    if (!isResidentialDepType(normalizedDepreciation.depType)) {
      return HAKLAI_DEPRECIATION_ERRORS.taxTrackRequired;
    }

    if (
      Object.prototype.hasOwnProperty.call(normalizedDepreciation, "depreciationBuiltPercent")
      && !isValidResidentialBuildingPercent(normalizedDepreciation.depreciationBuiltPercent)
    ) {
      return HAKLAI_DEPRECIATION_ERRORS.buildingPercentInvalid;
    }

    return "";
  }

  if (mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11) {
    return validateManualDepreciationAmount(normalizedDepreciation);
  }

  return "";
};

export const calculateHaklaiAutoDepreciation = ({
  amount,
  part = {},
  depreciation = {},
  acquisitionAmount = 0,
  clampStartDate = "",
  clampEndDate = ""
}) => {
  const normalizedAmount = normalizeNumericValue(amount);
  if (!(normalizedAmount > 0)) return null;

  const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
  const depreciationPercent = getNormalizedResidentialDepreciationPercent(normalizedDepreciation);
  if (!(depreciationPercent > 0)) return null;

  const depType = getNormalizedResidentialDepType(normalizedDepreciation);
  if (!depType) return null;

  const buildingPercent = getHaklaiResidentialDepreciationBuiltPercent({
    part,
    depreciation: normalizedDepreciation
  });
  if (!(buildingPercent > 0) || buildingPercent > 1) return null;

  const dateRanges = Array.isArray(normalizedDepreciation.dateRanges) ? normalizedDepreciation.dateRanges : [];
  if (!dateRanges.length) return null;

  let totalDepreciation = 0;
  dateRanges.forEach((range) => {
    if (!range?.startDate || !range?.endDate) return;

    let effectiveStart = range.startDate;
    if (depType === "ptor" && effectiveStart < PTOR_START_DATE) {
      effectiveStart = PTOR_START_DATE;
    }
    if (clampStartDate && effectiveStart < clampStartDate) {
      effectiveStart = clampStartDate;
    }

    let effectiveEnd = range.endDate;
    if (clampEndDate && effectiveEnd > clampEndDate) {
      effectiveEnd = clampEndDate;
    }
    if (effectiveStart > effectiveEnd) return;

    const startDate = parseClassicDepreciationDate(effectiveStart);
    const inclusiveEnd = parseClassicDepreciationDate(effectiveEnd);
    if (!startDate || !inclusiveEnd) return;

    inclusiveEnd.setDate(inclusiveEnd.getDate() + 1);
    const totalYears = Math.max(0, moment(inclusiveEnd).diff(moment(startDate), "years", true));
    totalDepreciation += normalizedAmount * depreciationPercent * buildingPercent * totalYears;
  });

  return Number.isFinite(totalDepreciation)
    ? parseFloat(totalDepreciation.toFixed(2))
    : null;
};
